import React, { useMemo, useState, useEffect } from "react";
import {
  Grid,
  Box,
  Typography,
  Button,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { PrimaryButton } from "../../Button";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../services/redux/store";
import postsServices from "../../../../services/api/service/postsService";
import {
  setActiveHorizontalIndex,
  setHorizontalSteps,
  setActiveVerticalIndex,
  updateFormData,
  setSummarySaveInProgress,
  bumpFormJsonRefreshNonce,
  setFetchUserApplicationData,
  setIsRefColBtnEnable,
} from "../../../../services/redux/slices/stepperDynamicFormSlice";
import { setStatusPopup } from "../../../../services/redux/slices/statusPopupSlice";
import dropdownService from "../../../../services/api/service/dropdownService";
import {numberToWord, applyCollateralAddressFromPincode, resolveCollateralAddressFieldNames, debounce, fieldBelongsToCollateralNumber, resolveCollateralFieldId, isCollateralContinueGenuinelySaved, getOwnerIndexFromCoOwnerField, buildCoOwnerNameOptions, getSelectedCoOwnerNamesForCollateral, isSavedOwnershipPercentageEdited} from "../../../../utils/helpers";
import ModalPopup from "../../ModalPopup";
import eligibilityCheckServices from "../../../../services/api/service/eligibilityCheckService";
// ─── Field name helpers ──────────────────────────────────────────────────────
// Collateral fields use prefix: collateral<Suffix>
// e.g. collateralType, collateralValue, collateralDescription, collateralOwner

// Fallback template used only the very first time "Add More" is clicked and
// no collateral1... fields exist yet to clone (the get-data API now sends
// only "Has Collateral?" by default, with no collateral fields until one is
// added). Field shape (names, casing, types, options) copied verbatim from
// an actual Collateral 1 field set returned by the backend.
const DEFAULT_COLLATERAL_TEMPLATE: Array<{
  label: string;
  name: string;
  type: string;
  required: boolean;
  options: { options: any[] } | null;
}> = [
  {
    label: "Collateral Type",
    name: "collateral1Type",
    type: "dropdown",
    required: true,
    options: {
      options: [
        { value: "307", label: "Fixed Deposit" },
        { value: "308", label: "Property" },
        { value: "309", label: "Savings" },
      ],
    },
  },
  { label: "Collateral Value", name: "collateral1Value", type: "number", required: true, options: null },
  { label: "Name FD Holder", name: "collateral1FdHolderName", type: "text", required: true, options: null },
  { label: "FD Number", name: "collateral1FdNumber", type: "text", required: true, options: null },
  { label: "Name Holder", name: "collateral1holderName", type: "text", required: true, options: null },
  { label: "Account Number", name: "collateral1accountNumber", type: "text", required: true, options: null },
  {
    label: "Co Owner Name",
    name: "coOwnerCollateral1Name",
    type: "dropdown",
    required: false,
    options: { options: [] },
  },
  { label: "Ownership Percentage", name: "coOwnerCollateral1OwnershipPercentage", type: "number", required: false, options: null },
  { label: "Phone Number", name: "coOwnerCollateral1PhoneNumber", type: "phone", required: false, options: null },
  { label: "Per Price Unit", name: "collateral1unitPrice", type: "number", required: false, options: null },
  { label: "Number of Units", name: "collateral1noOfUnits", type: "number", required: false, options: null },
  { label: "Total Value", name: "collateral1totalValue", type: "number", required: false, options: null },
  { label: "Property Type", name: "collateral1propertypeName", type: "autocomplete", required: false, options: null },
  { label: "Property Sub Type", name: "collateral1propertySubTypeName", type: "autocomplete", required: false, options: null },
  { label: "Address Type", name: "collateral1addressType", type: "autocomplete", required: false, options: null },
  { label: "Address Line 1", name: "collateral1addressLine1", type: "text", required: false, options: null },
  { label: "Address Line 2", name: "collateral1addressLine2", type: "text", required: false, options: null },
  { label: "Pincode", name: "collateral1pincode", type: "number", required: false, options: null },
  { label: "City", name: "collateral1city", type: "autocomplete", required: false, options: null },
  { label: "State", name: "collateral1state", type: "autocomplete", required: false, options: null },
  { label: "Country", name: "collateral1country", type: "autocomplete", required: false, options: null },
];

interface CollateralDetailsProps {
  fields: any[];
  renderField: (field: any) => React.ReactNode;
  formikRef: any;
  values?: any;
  isSaving?: boolean;
  onSaveAndNext?: (collateralNumber: number) => Promise<boolean | void>;
  expandedCollateral?: number | false;
  onExpandedChange?: (expanded: number | false) => void;
  hideInternalSaveButton?: boolean;
  pageType?:string;
  isDIYJourney?: boolean;
  // Per-collateral-group snapshot of field values as they stood at the
  // moment that group was last genuinely saved — owned/populated by
  // DynamicForm.tsx (the Save & Next handler there writes to it), shared
  // here so Add More/Remove can re-check isCollateralContinueGenuinelySaved
  // against the same data and keep the parent's Continue button in sync
  // immediately, instead of waiting on it to reconcile on its own.
  lastSavedCollateralSnapshotByGroupRef?: React.MutableRefObject<Record<number, string>>;
}

const CollateralDetails: React.FC<CollateralDetailsProps> = ({
  fields,
  renderField,
  formikRef,
  values,
  isSaving,
  onSaveAndNext,
  expandedCollateral,
  onExpandedChange,
  hideInternalSaveButton,
  pageType,
  isDIYJourney = false,
  lastSavedCollateralSnapshotByGroupRef,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  // Re-checks whether the Reference & Collateral step's Continue button is
  // still genuinely earned, using the exact same rule DynamicForm's own
  // reconcile effect uses (see isCollateralContinueGenuinelySaved), and
  // pushes the answer to Redux right away. Called right after Add
  // More/Remove below — those don't always change something DynamicForm's
  // effect is watching quickly enough (e.g. Collateral 1's field keys are
  // never removed from horizontalSteps, only blanked), so without this the
  // button could sit stale until an unrelated re-render happened to
  // reconcile it. `valueOverrides` folds in field changes made just before
  // this call (e.g. the values a remove/add just set) — formikRef.current.values
  // won't reflect a setFieldValue call made earlier in the SAME synchronous
  // handler yet (Formik only updates it once React actually re-renders), so
  // reading it blind here would check against pre-change data.
  const syncContinueButtonEnabled = (valueOverrides?: Record<string, any>) => {
    const vals = { ...(formikRef?.current?.values || {}), ...(valueOverrides || {}) };
    const isGenuinelySaved = isCollateralContinueGenuinelySaved(
      vals,
      lastSavedCollateralSnapshotByGroupRef?.current || {},
      fetchUserApplicationData,
    );
    dispatch(setIsRefColBtnEnable(isGenuinelySaved));
  };
  const formValues = values || formikRef?.current?.values || {};
  
  const { trackingId, horizontalSteps, activeHorizontalIndex, activeVerticalIndex, fetchUserApplicationData } =
    useSelector((state: RootState) => state.stepper);
  // Student Details is horizontalSteps[1].verticalSteps[0] — once it has data, FD/Savings
  // Holder Name should no longer be editable.
  const isStudentStepCompleted = Boolean(
    horizontalSteps?.[1]?.verticalSteps?.[0]?.fields?.length,
  );

  const getNormalized = (name: any) =>
    String(name || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  // fetchUserApplicationData.horizontalSteps is the raw fetch-application-data
  // response — each field there is a single-key `{ [fieldName]: savedValue }`
  // object, unlike state.stepper.horizontalSteps (field config only, no
  // values) or Formik's live/initial values (which can't be trusted here:
  // initialValues doesn't reliably reflect what's actually saved server-side
  // once the form has been reinitialized/edited). Scan every step for the
  // field name directly against this fetched data.
  const getFetchedFieldValue = (fieldName: string): any => {
    const steps = fetchUserApplicationData?.horizontalSteps;
    if (!Array.isArray(steps)) return undefined;
    for (const hStep of steps) {
      const vSteps = hStep?.verticalSteps;
      if (!Array.isArray(vSteps)) continue;
      for (const vStep of vSteps) {
        const stepFields = vStep?.fields;
        if (!Array.isArray(stepFields)) continue;
        for (const field of stepFields) {
          if (field && typeof field === "object" && fieldName in field) {
            return field[fieldName];
          }
        }
      }
    }
    return undefined;
  };

  const normalizedCollateralFields = useMemo(() => {
    if (!Array.isArray(fields)) return [];
    return fields
      .map((f: any, idx: number) => ({ f, idx }))
      .filter(({ f }: any) => {
        const normalized = getNormalized(f?.name);
        return (
          normalized.startsWith("collateral") ||
          normalized.startsWith("coownercollateral")
        );
      })
      .sort((a: any, b: any) => {
        const aOrder =
          typeof a?.f?.fieldOrder === "number"
            ? a.f.fieldOrder
            : Number.MAX_SAFE_INTEGER;
        const bOrder =
          typeof b?.f?.fieldOrder === "number"
            ? b.f.fieldOrder
            : Number.MAX_SAFE_INTEGER;

        if (aOrder !== bOrder) return aOrder - bOrder;
        return a.idx - b.idx;
      })
      .map(({ f }: any) => f);
  }, [fields]);

  const hasApiFieldValue = (value: any) => {
    if (value === null || value === undefined) return false;
    if (typeof value === "object") {
      const optionValue = value?.value ?? value?.label;
      return String(optionValue ?? "").trim() !== "";
    }
    return String(value).trim() !== "";
  };

  // A co-owner already saved server-side (present in the fetch-application-data
  // response, or saved via Save & Next in this session) can't be removed, so
  // its Remove button is hidden.
  const isCoOwnerSectionFetched = (sectionFields: any[]) =>
    sectionFields.some(
      (field: any) =>
        hasApiFieldValue(getFetchedFieldValue(field?.name)) ||
        (savedCoOwnerFieldNames.has(field?.name) &&
          hasApiFieldValue(formValues?.[field?.name])),
    );

  const isCoOwnerPercentageField = (name: any) =>
    /^coOwnerCollateral.*OwnershipPercentage$/i.test(String(name || ""));

  // ── Single-row Ownership Percentage lock ──────────────────────────────────
  // All co-owners start editable. Once ONE saved co-owner's percentage is
  // changed from its saved value, every other co-owner's percentage in that
  // collateral is disabled; reverting it to the saved value unlocks them
  // again. The save payload then carries only that co-owner (see
  // buildCoOwnerOwnershipDetailsList). The total <= 100 check stays in
  // getReferenceCollateralValidationErrors.
  const getEditedCoOwnerPercentageFieldName = (sections: any[][]): string | null => {
    for (const sectionFields of sections) {
      const percentageField = sectionFields.find((f: any) =>
        isCoOwnerPercentageField(f?.name),
      );
      if (!percentageField?.name) continue;
      const savedValue =
        percentageField.name in savedOwnershipPercentages
          ? savedOwnershipPercentages[percentageField.name]
          : getFetchedFieldValue(percentageField.name);
      if (isSavedOwnershipPercentageEdited(formValues?.[percentageField.name], savedValue)) {
        return percentageField.name;
      }
    }
    return null;
  };

  const withCoOwnerPercentageLock = (field: any, editedFieldName: string | null) =>
    editedFieldName &&
    isCoOwnerPercentageField(field?.name) &&
    field.name !== editedFieldName
      ? { ...field, disabled: true }
      : field;

  // SUMMARY only: once Add More has added a co-owner that isn't saved yet, the
  // already-saved co-owners above it keep their Ownership Percentage locked
  // until the new one is saved or removed.
  const withSavedCoOwnerPercentageLockOnAdd = (
    field: any,
    sectionFields: any[],
    sections: any[][],
  ) =>
    pageType === "SUMMARY" &&
    isCoOwnerPercentageField(field?.name) &&
    isCoOwnerSectionFetched(sectionFields) &&
    sections.some((section) => !isCoOwnerSectionFetched(section))
      ? { ...field, disabled: true }
      : field;

  // Add More is disabled once the applicant and every co-applicant are
  // already selected as co-owners of this collateral, or once there are
  // already as many co-owner sections as applicant + co-applicants.
  const areAllCoOwnerNamesSelected = (
    collateralNumber: number,
    coOwnerSectionCount: number,
  ) => {
    const nameOptions = buildCoOwnerNameOptions(formValues);
    if (nameOptions.length === 0) return false;
    if (coOwnerSectionCount >= nameOptions.length) return true;
    const selectedNames = getSelectedCoOwnerNamesForCollateral(
      formValues,
      collateralNumber,
    ).map((name) => name.toLowerCase());
    return nameOptions.every((opt) =>
      selectedNames.includes(String(opt.value || "").trim().toLowerCase()),
    );
  };

  const fetchedCollateralTypeMap = useMemo(() => {
    const locked: Record<number, boolean> = {};
    // In the SUMMARY edit flow (ReferenceCollateralDetails -> CollateralDetails),
    // `fields` are plain field-config objects sourced from state.stepper.horizontalSteps
    // — they never carry an embedded saved value the way the stepper flow's fetched
    // fields sometimes do. Fall back to the raw fetch-application-data response
    // (fetchUserApplicationData) for the actual saved value in that case.
    normalizedCollateralFields.forEach((field: any) => {
      const match = getNormalized(field?.name).match(/^collateral(\d*)type$/);
      if (!match) return;
      const apiValue =
        field?.fieldValue ??
        field?.formFieldValue ??
        field?.field_value ??
        field?.form_field_value ??
        field?.value ??
        field?.savedValue ??
        field?.answer ??
        field?.selectedValue ??
        getFetchedFieldValue(field?.name);
      if (hasApiFieldValue(apiValue)) {
        locked[Number(match[1] || 1)] = true;
      }
    });
    return locked;
  }, [normalizedCollateralFields, fetchUserApplicationData]);

  // "Has Collateral?" is a standalone gate field, not part of any numbered
  // collateral group — the API now sends it by itself by default, with no
  // collateral1... fields until the applicant answers Yes and adds one.
  const hasCollateralField = useMemo(
    () =>
      Array.isArray(fields)
        ? fields.find((f: any) => getNormalized(f?.name) === "hascollateral")
        : undefined,
    [fields],
  );
  const hasCollateralRawValue = hasCollateralField
    ? formValues?.[hasCollateralField.name]
    : undefined;
  const hasCollateralValue =
    hasCollateralRawValue && typeof hasCollateralRawValue === "object"
      ? (hasCollateralRawValue as any).value ?? (hasCollateralRawValue as any).label
      : hasCollateralRawValue;
  const isHasCollateralYes =
    String(hasCollateralValue ?? "").trim().toLowerCase() === "yes";
  const isHasCollateralNo =
    String(hasCollateralValue ?? "").trim().toLowerCase() === "no";

  // Default "Has Collateral?" to "No" until the applicant explicitly picks
  // an option, rather than leaving the radio unselected.
  useEffect(() => {
    if (!hasCollateralField || !formikRef?.current) return;
    const currentValue = formikRef.current.values?.[hasCollateralField.name];
    if (currentValue === undefined || currentValue === null || String(currentValue).trim() === "") {
      formikRef.current.setFieldValue(hasCollateralField.name, "No", false);
      dispatch(updateFormData({ [hasCollateralField.name]: "No" }));
    }
  }, [hasCollateralField, formikRef, dispatch]);

  const [pendingAddedFields, setPendingAddedFields] = useState<any[]>([]);
  const [pendingRemovedFieldNames, setPendingRemovedFieldNames] = useState<
    Record<string, true>
  >({});

  const collateralFields = useMemo(() => {
    const removedNames = new Set(Object.keys(pendingRemovedFieldNames));
    const baseFields = normalizedCollateralFields.filter(
      (f: any) => !removedNames.has(String(f?.name || "")),
    );
    const existingNames = new Set(
      baseFields.map((f: any) => String(f?.name || "")),
    );
    const optimisticFields = pendingAddedFields.filter(
      (f: any) => !existingNames.has(String(f?.name || "")),
    );
    return [...baseFields, ...optimisticFields];
  }, [normalizedCollateralFields, pendingAddedFields, pendingRemovedFieldNames]);

  useEffect(() => {
    if (pendingAddedFields.length === 0) return;
    const serverNames = new Set(
      normalizedCollateralFields.map((f: any) => String(f?.name || "")),
    );
    setPendingAddedFields((prev) =>
      prev.filter((f: any) => !serverNames.has(String(f?.name || ""))),
    );
  }, [normalizedCollateralFields, pendingAddedFields.length]);

  useEffect(() => {
    const pendingRemovedNames = Object.keys(pendingRemovedFieldNames);
    if (pendingRemovedNames.length === 0) return;
    const serverNames = new Set(
      normalizedCollateralFields.map((f: any) => String(f?.name || "")),
    );
    setPendingRemovedFieldNames((prev) => {
      const next: Record<string, true> = {};
      Object.keys(prev).forEach((name) => {
        if (serverNames.has(name)) {
          next[name] = true;
        }
      });
      return next;
    });
  }, [normalizedCollateralFields, pendingRemovedFieldNames]);

  const getCollateralNumber = (name: any): number => {
    const normalized = getNormalized(name);
    const match = normalized.match(/^(?:collateral|coownercollateral)(\d+)/);
    return match ? Number(match[1]) : 1;
  };

  const collateralNumbers = useMemo(() => {
    const nums = new Set<number>();
    collateralFields.forEach((f: any) => {
      const normalized = getNormalized(f?.name);
      // Only count base collateral fields (not co-owner) to determine section numbers.
      // Co-owner fields use names like coOwnerCollateral1_2Name where the owner index
      // is appended to the collateral number, which would produce false collateral numbers.
      if (!normalized.startsWith("coownercollateral")) {
        nums.add(getCollateralNumber(f?.name));
      }
    });
    if (!nums.size && collateralFields.length > 0) nums.add(1);
    return Array.from(nums).sort((a, b) => a - b);
  }, [collateralFields]);

  const isFieldValueFilled = (raw: any): boolean => {
    if (raw === null || raw === undefined) return false;
    if (typeof raw === "object") {
      const v = (raw as any)?.value ?? (raw as any)?.label;
      return String(v ?? "").trim() !== "";
    }
    return String(raw).trim() !== "";
  };

  // Collateral 1's field keys can exist (e.g. from the get-data API's
  // default schema) with nothing actually filled in yet — e.g. right after
  // "removing" it, which (see removeCollateralFields) only clears its
  // values, not its field keys. Track which collateral numbers actually
  // have real data, separately from which ones merely have field keys.
  const collateralNumbersWithData = useMemo(
    () =>
      collateralNumbers.filter((n) =>
        collateralFields.some(
          (f: any) =>
            getCollateralNumber(f?.name) === n &&
            isFieldValueFilled(formValues?.[f?.name]),
        ),
      ),
    [collateralNumbers, collateralFields, formValues],
  );

  // A group counts as a "real" collateral (something the applicant would
  // consider an actual, currently-relevant collateral) if it's any number
  // other than 1, or if it's 1 AND it actually has data. Collateral 1's
  // field keys are permanent scaffolding (see removeCollateralFields) —
  // present in the schema even with nothing ever filled in (e.g. the
  // get-data API returning both Collateral 1 and 2's field keys while the
  // fetched data has neither filled in, showing only the last/highest
  // group in the UI) — so its bare existence alone must never count as
  // "another collateral still exists" the way an actually-added group 2+
  // does.
  const realCollateralNumbers = useMemo(
    () =>
      collateralNumbers.filter(
        (n) => n !== 1 || collateralNumbersWithData.includes(1),
      ),
    [collateralNumbers, collateralNumbersWithData],
  );

  // DIY journey only: once every collateral has actually lost its data
  // (the guard below is what lets that happen while Yes is selected), move
  // "Has Collateral?" to No automatically instead of leaving Yes selected
  // with nothing behind it. Tracks collateralNumbersWithData rather than
  // raw field-key presence, since Collateral 1's keys stay behind after removal.
  const previousCollateralWithDataCountRef = React.useRef<number | undefined>(undefined);
  // Shared with the auto-create-on-Yes effect further below: both effects
  // run in the same pass off the same count change, so without this flag
  // the auto-create effect would still see the stale (pre-switch)
  // isHasCollateralYes = true and immediately recreate a blank Collateral 1
  // right after this effect just switched the answer to No.
  const justAutoSwitchedToNoRef = React.useRef(false);
  useEffect(() => {
    if (!isDIYJourney || !hasCollateralField) return;
    const previousCount = previousCollateralWithDataCountRef.current;
    previousCollateralWithDataCountRef.current = collateralNumbersWithData.length;
    if (previousCount === undefined) return;
    // collateralNumbersWithData hitting 0 only means no group has DATA —
    // e.g. Collateral 1 (filled) removed while an "Add More"-generated but
    // still-blank Collateral 2 exists: 2 has no data either, so the data
    // count drops to 0 even though Collateral 2's group is still there.
    // Also require realCollateralNumbers.length === 0 — Collateral 1's bare,
    // permanently-present field keys don't count as "another collateral
    // still exists" (see realCollateralNumbers above), so a still-existing
    // Collateral 2+ correctly keeps this from auto-switching to No, while a
    // state with nothing but Collateral 1's empty placeholder does not.
    if (
      previousCount > 0 &&
      collateralNumbersWithData.length === 0 &&
      realCollateralNumbers.length === 0 &&
      isHasCollateralYes
    ) {
      justAutoSwitchedToNoRef.current = true;
      formikRef?.current?.setFieldValue(hasCollateralField.name, "No", false);
      dispatch(updateFormData({ [hasCollateralField.name]: "No" }));
    }
  }, [
    isDIYJourney,
    hasCollateralField,
    collateralNumbersWithData.length,
    realCollateralNumbers.length,
    isHasCollateralYes,
    formikRef,
    dispatch,
  ]);

  // Switching "Has Collateral?" to No while a collateral group still has
  // real data is not allowed — the applicant must remove every collateral
  // first. Field keys with no data behind them (see above) don't block this.
  const previousHasCollateralValueRef = React.useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!hasCollateralField) return;
    const previous = previousHasCollateralValueRef.current;
    const current = String(hasCollateralValue ?? "").trim().toLowerCase();
    previousHasCollateralValueRef.current = current;
    if (previous === undefined) return;
    if (previous !== "no" && current === "no" && collateralNumbersWithData.length > 0) {
      dispatch(
        setStatusPopup({
          status: "info",
          message: "Please delete all collateral and then select No.",
        }),
      );
      formikRef?.current?.setFieldValue(hasCollateralField.name, "Yes", false);
      dispatch(updateFormData({ [hasCollateralField.name]: "Yes" }));
    }
  }, [
    hasCollateralField,
    hasCollateralValue,
    collateralNumbersWithData.length,
    formikRef,
    dispatch,
  ]);

  // No fallback to [1] here: with only "Has Collateral?" present and no
  // collateral1... fields yet, there is nothing to show a section for —
  // the collapsible Collateral accordion should stay hidden until the
  // applicant answers Yes and adds one via "Add More".
  //
  // DIY journey only: the get-data API's field config (collateralFields)
  // and the fetch-application-data response (fetchUserApplicationData) can
  // disagree — e.g. Collateral 1 still exists as a field-config default but
  // was never actually confirmed by a fetch, while Collateral 2 was. When
  // that's the ONLY collateral present, still show it (it's the applicant's
  // one chance to fill it in). But once a second, fetch-confirmed collateral
  // exists, an unconfirmed Collateral 1 is just a stale default slot — hide
  // it rather than showing an empty section alongside the real one.
  const displayedCollateralNumbers = useMemo(() => {
    if (!isDIYJourney) return collateralNumbers;
    // DIY: the section only ever loads once "Has Collateral?" = Yes,
    // regardless of what collateral field data still exists underneath.
    if (!isHasCollateralYes) return [];
    if (collateralNumbers.length <= 1) return collateralNumbers;
    // Don't hide Collateral 1 just because it isn't fetch-confirmed yet — it
    // can also be genuinely unconfirmed because the applicant is still
    // filling it in (unsaved) when Add More creates Collateral 2's field
    // keys. Only hide it when it also has no live (Formik) data either,
    // i.e. it's truly a stale/deleted slot, not one mid-entry.
    if (
      collateralNumbers.includes(1) &&
      !fetchedCollateralTypeMap[1] &&
      !collateralNumbersWithData.includes(1)
    ) {
      return collateralNumbers.filter((n) => n !== 1);
    }
    return collateralNumbers;
  }, [
    collateralNumbers,
    isDIYJourney,
    isHasCollateralYes,
    fetchedCollateralTypeMap,
    collateralNumbersWithData,
  ]);

  const [localExpanded, setLocalExpanded] = useState<number | false>(
    displayedCollateralNumbers[0] ?? 1,
  );
  const expanded =
    expandedCollateral !== undefined ? expandedCollateral : localExpanded;
  const setExpanded = (next: number | false) => {
    onExpandedChange?.(next);
    if (expandedCollateral === undefined) {
      setLocalExpanded(next);
    }
  };
  const [visibleCoOwnerCount, setVisibleCoOwnerCount] = useState<Record<number, number>>({});
  const [isAddingCoOwner, setIsAddingCoOwner] = useState<Record<number, boolean>>({});
  const [isRemovingCoOwner, setIsRemovingCoOwner] = useState<Record<string, boolean>>({});
  const [isAddingCollateral, setIsAddingCollateral] = useState(false);
  const [isRemovingCollateral, setIsRemovingCollateral] = useState<Record<number, boolean>>({});
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [isConfirmRemoving, setIsConfirmRemoving] = useState(false);
  const [pendingRemove, setPendingRemove] = useState<
    | { kind: "collateral"; collateralNumber: number }
    | {
        kind: "coOwner";
        collateralNumber: number;
        sectionFields: any[];
        totalSections: number;
        sectionIdx: number;
        removeKey: string;
      }
    | null
  >(null);
  const [addressTypeOptions, setAddressTypeOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [propertyTypeOptions, setPropertyTypeOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [propertySubTypeOptions, setPropertySubTypeOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [selectedPropertyType, setSelectedPropertyType] = useState<string>("");
  const [savedCollateralTypeMap, setSavedCollateralTypeMap] = useState<Record<number, boolean>>({});
  const [savedCoOwnerFieldNames, setSavedCoOwnerFieldNames] = useState<Set<string>>(new Set());
  // Co-owner Ownership Percentage values as of this session's last Save & Next,
  // keyed by field name — overrides the (possibly stale) fetched value as the
  // baseline the single-row edit lock compares against.
  const [savedOwnershipPercentages, setSavedOwnershipPercentages] = useState<Record<string, any>>({});

  useEffect(() => {
    const handleCollateralSaved = (event: Event) => {
      const collateralNumber = Number((event as CustomEvent)?.detail?.collateralNumber || 0);
      if (!collateralNumber) return;
      setSavedCollateralTypeMap((prev) => ({ ...prev, [collateralNumber]: true }));
      // Remember which co-owner fields of this collateral were saved with a value.
      const latestValues = formikRef?.current?.values || {};
      const savedPercentages: Record<string, any> = {};
      Object.keys(latestValues).forEach((name) => {
        if (
          isCoOwnerPercentageField(name) &&
          fieldBelongsToCollateralNumber(name, collateralNumber)
        ) {
          savedPercentages[name] = latestValues[name];
        }
      });
      setSavedOwnershipPercentages((prev) => ({ ...prev, ...savedPercentages }));
      const savedNames = Object.keys(latestValues).filter((name) => {
        const match = name.match(/^coOwnerCollateral(\d+)(?:_\d+)?[A-Za-z]/i);
        return (
          Number(match?.[1]) === collateralNumber &&
          hasApiFieldValue(latestValues[name])
        );
      });
      if (savedNames.length > 0) {
        setSavedCoOwnerFieldNames((prev) => new Set(Array.from(prev).concat(savedNames)));
      }
    };

    window.addEventListener("collateral:saved", handleCollateralSaved);
    return () => window.removeEventListener("collateral:saved", handleCollateralSaved);
  }, []);

  // ── Fetch Address Types, Property Types, Property Sub Types ───────────────
  useEffect(() => {
    let isMounted = true;
    dropdownService.getAddressTypes().then((data) => {
      if (isMounted && data.length > 0) setAddressTypeOptions(data);
    }).catch(() => {});
    dropdownService.getPropertyTypes().then((data) => {
      if (isMounted && data.length > 0) setPropertyTypeOptions(data);
    }).catch(() => {});
    dropdownService.getPropertySubTypes().then((data) => {
      if (isMounted && data.length > 0) setPropertySubTypeOptions(data);
    }).catch(() => {});
    return () => { isMounted = false; };
  }, []);

  // Re-fetch sub-types when selected property type changes
  useEffect(() => {
    if (!selectedPropertyType) return;
    let isMounted = true;
    dropdownService.getPropertySubTypes(selectedPropertyType).then((data) => {
      if (isMounted) setPropertySubTypeOptions(data);
    }).catch(() => {});
    return () => { isMounted = false; };
  }, [selectedPropertyType]);

  // Watch collateral1propertyName to trigger sub-type re-fetch
  useEffect(() => {
    const raw = formValues?.["collateral1propertyName"];
    const val = raw && typeof raw === "object" ? raw?.value : raw;
    if (val && String(val) !== selectedPropertyType) {
      setSelectedPropertyType(String(val));
    }
  }, [formValues?.["collateral1propertyName"]]);

  const getFieldsByGroup = (n: number) => {
    // Plain startsWith(`collateral${n}`) also matches longer numbers that
    // happen to share the same leading digits (e.g. group 1 matching
    // collateral10/11/12/... fields too, since "collateral12Type" starts
    // with "collateral1") — fieldBelongsToCollateralNumber parses the full
    // number instead, so each field is only ever counted in its own group.
    const base = collateralFields.filter((f: any) => {
      const normalized = getNormalized(f?.name);
      if (normalized.startsWith("coownercollateral")) return false;
      return fieldBelongsToCollateralNumber(f?.name, n);
    });

    const coOwner = collateralFields.filter((f: any) => {
      const normalized = getNormalized(f?.name);
      if (!normalized.startsWith("coownercollateral")) return false;
      return fieldBelongsToCollateralNumber(f?.name, n);
    });

    return { base, coOwner };
  };

  const findBaseFieldByToken = (baseFields: any[], token: string) =>
    baseFields.find((f: any) => getNormalized(f?.name).includes(token));

  const getNumericFormValue = (value: unknown): number | null => {
    const raw =
      value && typeof value === "object" && value !== null && "value" in value
        ? (value as { value: unknown }).value
        : value;
    const normalized = String(raw ?? "").trim();
    if (!normalized) return null;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const getAutoComputedPropertyTotal = (
    baseFields: any[],
    latestValues: Record<string, any>,
  ) => {
    if (getCollateralMode(baseFields) !== "property") return null;

    const unitPriceField = findBaseFieldByToken(baseFields, "unitprice");
    const noOfUnitsField = findBaseFieldByToken(baseFields, "noofunits");
    const totalValueField = findBaseFieldByToken(baseFields, "totalvalue");
    if (!unitPriceField || !noOfUnitsField || !totalValueField) return null;

    const unitPrice = getNumericFormValue(latestValues?.[unitPriceField.name]);
    const noOfUnits = getNumericFormValue(latestValues?.[noOfUnitsField.name]);
    if (unitPrice === null || noOfUnits === null) return null;

    return {
      totalValueFieldName: totalValueField.name,
      computedTotal: String(unitPrice * noOfUnits),
    };
  };

  useEffect(() => {
    if (!formikRef?.current) return;

    displayedCollateralNumbers.forEach((collateralNumber) => {
      const { base } = getFieldsByGroup(collateralNumber);
      const autoTotal = getAutoComputedPropertyTotal(
        base,
        formikRef.current?.values || formValues,
      );
      if (!autoTotal) return;

      const currentValue = String(
        (formikRef.current?.values || formValues)?.[autoTotal.totalValueFieldName] ??
          "",
      );
      if (currentValue === autoTotal.computedTotal) return;

      formikRef.current.setFieldValue(
        autoTotal.totalValueFieldName,
        autoTotal.computedTotal,
        false,
      );
      dispatch(
        updateFormData({
          [autoTotal.totalValueFieldName]: autoTotal.computedTotal,
        }),
      );
    });
  }, [formValues, collateralFields, displayedCollateralNumbers, dispatch, formikRef]);

  const hasToken = (field: any, tokens: string[]) => {
    const normalizedName = getNormalized(field?.name);
    const normalizedLabel = getNormalized(field?.label);

    const fieldNameWithoutCollateralPrefix = normalizedName.replace(
      /^collateral\d+/,
      "",
    );

    return tokens.some((token) => {
      const normalizedToken = getNormalized(token);

      return (
        fieldNameWithoutCollateralPrefix === normalizedToken ||
        normalizedLabel === normalizedToken
      );
    });
  };

  const isCollateralTypeField = (f: any) => {
    const n = getNormalized(f?.name);
    return /^collateral\d*type$/.test(n) || getNormalized(f?.label).includes("collateraltype");
  };

  const getCollateralMode = (sectionFields: any[]): "fixed" | "savings" | "property" | null => {
    // Match collateralType, collateral1Type, collateral2Type, etc. — by regex on name, or by label substring
    const typeField = sectionFields.find(isCollateralTypeField);

    if (!typeField) return null;

    const raw = formValues?.[typeField?.name];
    const rawValue =
      raw && typeof raw === "object" && "value" in raw ? raw.value : raw;
    const rawLabel =
      raw && typeof raw === "object" && "label" in raw ? raw.label : raw;

    const normalized = getNormalized(rawLabel || rawValue);
    const normalizedValue = String(rawValue ?? "").trim();

    if (
      normalizedValue === "307" ||
      normalized.includes("fixed") ||
      normalized.includes("fd")
    ) {
      return "fixed";
    }

    if (normalizedValue === "309" || normalized.includes("saving")) {
      return "savings";
    }

    if (normalizedValue === "308" || normalized.includes("property")) {
      return "property";
    }

    return null;
  };

  const getVisibleBaseFields = (baseFields: any[]) => {
    const mode = getCollateralMode(baseFields);

    if (!mode) {
      // No Collateral Type selected yet — show only the Type field until one is chosen.
      // If this section has no Type field at all (unexpected data shape), fall back to
      // showing everything rather than hiding the whole section.
      const typeFields = baseFields.filter(isCollateralTypeField);
      return typeFields.length > 0 ? typeFields : baseFields;
    }
    
    const commonTokens = ["collateraltype","collateralvalue"];

    // Override hasToken for the type field since "collateral1type".includes("collateraltype") = false
    const isTypeOrValueField = (f: any) => {
      const n = getNormalized(f?.name);
      return /^collateral\d*type$/.test(n) || /^collateral\d*value$/.test(n) ||
        hasToken(f, commonTokens);
    };
    const fixedTokens = [
      "fdholdername",
      "fdnumber",
    ];
    const savingsTokens = [
      "holdername",
      "accountnumber",
    ];
    
    const propertyTokens = [
      "unitprice",
      "noofunits",
      "totalvalue",
      "propertypename",
      "propertysubtypename",
      "addresstype",
      "addressline1",
      "addressline2",
      "landmark",
      "area",
      "pincode",
      "city",
      "state",
      "country",
    ];

    const tokenMap: Record<"fixed" | "savings" | "property", string[]> = {
      fixed: fixedTokens,
      savings: savingsTokens,
      property: propertyTokens,
    };

    return baseFields.filter((field: any) => isTypeOrValueField(field) || hasToken(field, tokenMap[mode]));
  };

  const lastCollateralPincodeRef = React.useRef<Record<string, string>>({});

  const fetchCollateralAddressByPincode = useMemo(
    () =>
      debounce(async (collateralNumber: number, pincode: string) => {
        if (!formikRef?.current || String(pincode).length !== 6) return;

        try {
          const resp = await eligibilityCheckServices.getZipCode(pincode);
          if (resp?.status !== 200 || !resp?.data?.data?.length) {
            applyCollateralAddressFromPincode(formikRef, {
              fields: collateralFields,
              collateralNumber,
              clear: true,
              onUpdate: (patch) => dispatch(updateFormData(patch)),
            });
            return;
          }

          const { cityName, countryName, stateName }: any =
            resp.data.data[0] || {};

          let countries: Array<{ value?: string; label?: string }> = [];
          try {
            const countriesResp: any = await dropdownService.getCountries();
            const rawCountries = Array.isArray(countriesResp?.data)
              ? countriesResp.data
              : Array.isArray(countriesResp)
                ? countriesResp
                : [];
            countries = rawCountries
              .map((item: any) => ({
                value: String(
                  item?.value ?? item?.countryId ?? item?.countryDesc ?? "",
                ),
                label: String(
                  item?.label ?? item?.countryDesc ?? item?.countryId ?? "",
                ),
              }))
              .filter((opt: any) => opt.value || opt.label);
          } catch {
            countries = [];
          }

          applyCollateralAddressFromPincode(formikRef, {
            fields: collateralFields,
            collateralNumber,
            cityName: cityName || "",
            stateName: stateName || "",
            countryName: countryName || "",
            countries,
            onUpdate: (patch) => dispatch(updateFormData(patch)),
          });
        } catch (error) {
          console.error("Collateral pincode lookup failed:", error);
        }
      }, 400),
    [collateralFields, dispatch, formikRef],
  );

  useEffect(() => {
    displayedCollateralNumbers.forEach((collateralNumber) => {
      const { base } = getFieldsByGroup(collateralNumber);
      if (getCollateralMode(base) !== "property") return;

      const addressFields = resolveCollateralAddressFieldNames(
        base,
        formikRef?.current?.values || formValues,
        collateralNumber,
      );
      if (!addressFields.pincode) return;

      const rawPincode =
        (formikRef?.current?.values || formValues)?.[addressFields.pincode];
      const pincode = String(
        rawPincode && typeof rawPincode === "object" && "value" in rawPincode
          ? (rawPincode as any).value
          : rawPincode ?? "",
      ).trim();

      const cacheKey = String(collateralNumber);
      if (lastCollateralPincodeRef.current[cacheKey] === pincode) return;
      lastCollateralPincodeRef.current[cacheKey] = pincode;

      if (pincode.length === 6) {
        fetchCollateralAddressByPincode(collateralNumber, pincode);
      }
    });
  }, [
    formValues,
    collateralFields,
    displayedCollateralNumbers,
    fetchCollateralAddressByPincode,
    dispatch,
    formikRef,
  ]);

  const splitCoOwnerSections = (coOwnerFields: any[]) => {
    if (!Array.isArray(coOwnerFields) || coOwnerFields.length === 0) return [];

    // Bucket by each field's OWN owner index (parsed from its "_N" suffix,
    // or 1 for the un-suffixed default) rather than by scan order (start a
    // new section whenever the next Name field is seen, sweep everything
    // after it into that section). Scan-order grouping assumed fields
    // always arrive interleaved as
    // [Name1,Percentage1,Phone1,Name2,Percentage2,Phone2,...] — true for
    // fields added via "Add More" this session, but not guaranteed for
    // fields coming back from a fresh fetch (ordered by the backend's own
    // fieldOrder, not owner grouping). When that assumption broke, one
    // owner's Name field landed alone in its own section while its
    // Percentage/Phone (and everyone after it) got swept into a LATER
    // owner's section — showing one owner's value under a different
    // owner's heading. Bucketing by each field's own parsed index (via the
    // shared getOwnerIndexFromCoOwnerField, so validation elsewhere sees
    // the exact same grouping) is immune to array order entirely, and
    // sorting the bucket keys keeps "Co-Owner N" numbering stable
    // regardless of fetch order, same as before.
    const sectionsByOwnerIndex = new Map<number, any[]>();
    coOwnerFields.forEach((field: any) => {
      const ownerIndex = getOwnerIndexFromCoOwnerField(field?.name);
      sectionsByOwnerIndex.set(ownerIndex, [
        ...(sectionsByOwnerIndex.get(ownerIndex) || []),
        field,
      ]);
    });

    return Array.from(sectionsByOwnerIndex.keys())
      .sort((a, b) => a - b)
      .map((ownerIndex) => sectionsByOwnerIndex.get(ownerIndex) as any[]);
  };

  // The only three field kinds a co-owner section ever has. Matching against
  // this fixed list (rather than stripping leading digits with a regex) is
  // immune to however many collateral-number/owner-index digits the template
  // field's name already carries — a digit-stripping regex previously
  // produced malformed, doubly-suffixed names (e.g. "coOwnerCollateral2_2_1Name")
  // or, after a naive fix, could collide with an existing owner's field name
  // and silently overwrite it in Formik. Whitelisting the suffix sidesteps
  // both failure modes entirely.
  const CO_OWNER_FIELD_SUFFIXES = ["OwnershipPercentage", "PhoneNumber", "Name"];

  const renameCoOwnerField = (
    fieldName: string,
    collateralNumber: number,
    ownerIndex: number,
  ) => {
    // Expected format:
    // coOwnerCollateral1Name -> coOwnerCollateral1_2Name
    // coOwnerCollateral1OwnershipPercentage -> coOwnerCollateral1_2OwnershipPercentage
    // coOwnerCollateral1PhoneNumber -> coOwnerCollateral1_2PhoneNumber
    const normalized = String(fieldName || "");
    const matchedSuffix = CO_OWNER_FIELD_SUFFIXES.find((suffix) =>
      normalized.toLowerCase().endsWith(suffix.toLowerCase()),
    );
    const suffix =
      matchedSuffix ?? normalized.replace(/^coOwnerCollateral\d*(?:_\d+)?/i, "");

    return `coOwnerCollateral${collateralNumber}_${ownerIndex}${suffix}`;
  };

  const forgetSavedOwnershipPercentages = (fieldNames: Set<any>) =>
    setSavedOwnershipPercentages((prev) => {
      const next = { ...prev };
      fieldNames.forEach((name) => delete next[String(name)]);
      return next;
    });

  const removeCoOwnerSection = async (
    collateralNumber: number,
    sectionFields: any[],
    totalSections: number,
    sectionIdx: number,
  ) => {
    const removeKey = `${collateralNumber}-${sectionIdx}`;
    const fieldNames = new Set(sectionFields.map((f: any) => f?.name));
    const fieldIds = sectionFields
      .map((f: any) => resolveCollateralFieldId(f))
      .filter((id: any) => id !== undefined && id !== null);

    if (!trackingId || fieldIds.length === 0) {
      dispatch(
        setStatusPopup({
          status: "error",
          message: "Unable to remove Co-Owner fields right now. Please try again.",
        }),
      );
      return;
    }

    const currentVerticalIndex =
      typeof activeVerticalIndex === "object"
        ? activeVerticalIndex?.[activeHorizontalIndex] ?? 0
        : 0;

    setIsRemovingCoOwner((prev) => ({ ...prev, [removeKey]: true }));

    try {
      const resp = await postsServices.addPost("api/v1/product/delete-user-fields", {
        form_field_ids: fieldIds,
        trackingId,
      });

      if (resp?.data?.status !== "success") {
        dispatch(
          setStatusPopup({
            status: "error",
            message: "Unable to remove Co-Owner fields right now. Please try again.",
          }),
        );
        return;
      }

      const updatedHorizontalSteps = [...(horizontalSteps || [])];
      const targetHorizontal = { ...(updatedHorizontalSteps?.[activeHorizontalIndex] || {}) };
      const targetVerticalSteps = [...(targetHorizontal?.verticalSteps || [])];

      if (
        typeof currentVerticalIndex === "number" &&
        currentVerticalIndex >= 0 &&
        currentVerticalIndex < targetVerticalSteps.length
      ) {
        const targetStep = { ...targetVerticalSteps[currentVerticalIndex] };
        targetStep.fields = (targetStep.fields || []).filter(
          (f: any) => !fieldNames.has(f?.name),
        );
        targetVerticalSteps[currentVerticalIndex] = targetStep;
        targetHorizontal.verticalSteps = targetVerticalSteps;
        updatedHorizontalSteps[activeHorizontalIndex] = targetHorizontal;
        dispatch(setHorizontalSteps(updatedHorizontalSteps));
      }
      setPendingRemovedFieldNames((prev) => {
        const next = { ...prev };
        fieldNames.forEach((name) => {
          if (name) next[String(name)] = true;
        });
        return next;
      });
      setPendingAddedFields((prev) =>
        prev.filter((f: any) => !fieldNames.has(f?.name)),
      );

      if (formikRef?.current) {
        sectionFields.forEach((field: any) => {
          formikRef.current.setFieldValue(field.name, undefined, false);
        });
      }
      forgetSavedOwnershipPercentages(fieldNames);

      setVisibleCoOwnerCount((prev) => ({
        ...prev,
        [collateralNumber]: Math.max((prev[collateralNumber] ?? totalSections) - 1, 1),
      }));
    } catch (error: any) {
      dispatch(
        setStatusPopup({
          status: "error",
          message:
            error?.response?.data?.message ||
            "Unable to remove Co-Owner fields right now. Please try again.",
        }),
      );
    } finally {
      setIsRemovingCoOwner((prev) => ({ ...prev, [removeKey]: false }));
    }
  };

  const addCollateralFields = async () => {
    // Resolve the real horizontal/vertical index by scanning for the actual
    // "Collateral Details" step, mirroring how removeCollateralFields
    // already does this safely. The previous hardcoded [3]/[4] indices meant
    // that when a product's fetched data genuinely has no Collateral
    // Details step, this silently fell back to whatever step actually sits
    // at that position (e.g. Documents & Consent) and injected fabricated
    // collateral fields into it — which is what caused unrelated/duplicate
    // collateral sections to appear.
    let resolvedHorizontalIndex = -1;
    let resolvedVerticalIndex = -1;
    let verticalStepId: any;
    (horizontalSteps || []).forEach((hStep: any, hIdx: number) => {
      if (resolvedHorizontalIndex !== -1) return;
      const vIdx = (hStep?.verticalSteps || []).findIndex(
        (vStep: any) => vStep?.stepLabel === "Collateral Details",
      );
      if (vIdx !== -1) {
        resolvedHorizontalIndex = hIdx;
        resolvedVerticalIndex = vIdx;
        verticalStepId = hStep.verticalSteps[vIdx]?.id;
      }
    });

    if (!verticalStepId || !trackingId) {
      dispatch(setStatusPopup({ status: "error", message: "Unable to add Collateral fields right now. Please try again." }));
      return;
    }

    // Use collateral 1 base fields + co-owner fields as the template, renaming to collateral N.
    // Collateral 1 may have had multiple Co-Owner sections added to it — a
    // new collateral should always start with just one, so only clone the
    // first co-owner section (Name/Percentage/Phone), not every section
    // collateral 1 happens to have accumulated.
    const collateral1Group = getFieldsByGroup(1);
    const collateral1FirstCoOwnerSection =
      splitCoOwnerSections(collateral1Group.coOwner)[0] || [];
    // No collateral1 fields exist yet (first-ever "Add More" after "Has
    // Collateral?" = Yes, with nothing to clone from) — fall back to the
    // hardcoded default template so Collateral 1 can still be created.
    const collateral1Fields =
      collateral1Group.base.length > 0 || collateral1FirstCoOwnerSection.length > 0
        ? [...collateral1Group.base, ...collateral1FirstCoOwnerSection]
        : DEFAULT_COLLATERAL_TEMPLATE;

    const nextNum = displayedCollateralNumbers.length > 0
      ? Math.max(...displayedCollateralNumbers) + 1
      : 1;

    const renameToNext = (name: string, count: number): string => {
      // coOwnerCollateral1XYZ -> coOwnerCollateral2XYZ
      if (/^coOwnerCollateral/i.test(name)) {
        return name.replace(/^(coOwnerCollateral)(\d+)?/i, `$1${count + 1}`);
      }
      // collateral1XYZ -> collateral2XYZ, collateralXYZ -> collateral2XYZ
      return name.replace(/^(collateral)(\d+)?/i, `$1${count + 1}`);
    };

    let res = await eligibilityCheckServices.getCollateralIds(trackingId);
    let nextColNum: number = nextNum;
    if(res && res?.status === 200) {
      nextColNum = res?.data?.totalCount;
    }
    const apiFields = collateral1Fields.map((field: any) => ({
      label: field?.label,
      name: renameToNext(String(field?.name || ""), nextColNum),
      type: field?.type,
      required: field?.required,
      options: field?.options ?? null,
    }));

    setIsAddingCollateral(true);
    try {
      const resp = await postsServices.addPost("api/v1/product/store-user-fields", {
        vertical_step_id: verticalStepId,
        trackingId,
        fields: apiFields,
      });

      if (resp?.data?.status === "success" && Array.isArray(resp?.data?.data)) {
        const newFields = resp.data.data.map((f: any) => ({
          ...f,
          options: f?.options ?? null,
        }));

        const updatedHorizontalSteps = [...(horizontalSteps || [])];
        const targetHorizontal = { ...(updatedHorizontalSteps?.[resolvedHorizontalIndex] || {}) };
        const targetVerticalSteps = [...(targetHorizontal?.verticalSteps || [])];

        const targetStep = { ...targetVerticalSteps[resolvedVerticalIndex] };
        targetStep.fields = [...(targetStep.fields || []), ...newFields];
        targetVerticalSteps[resolvedVerticalIndex] = targetStep;
        targetHorizontal.verticalSteps = targetVerticalSteps;
        updatedHorizontalSteps[resolvedHorizontalIndex] = targetHorizontal;

        dispatch(setHorizontalSteps(updatedHorizontalSteps));
        setPendingAddedFields((prev) => {
          const byName = new Map<string, any>();
          [...prev, ...newFields].forEach((f: any) => {
            byName.set(String(f?.name || ""), f);
          });
          return Array.from(byName.values());
        });
        setPendingRemovedFieldNames((prev) => {
          const next = { ...prev };
          newFields.forEach((f: any) => {
            const name = String(f?.name || "");
            if (name in next) delete next[name];
          });
          return next;
        });

        if (formikRef?.current) {
          newFields.forEach((field: any) => {
            formikRef.current.setFieldValue(field.name, "", false);
          });
        }

        setExpanded(nextNum);
        // The new group starts blank, so this can only ever turn Continue
        // OFF (if the applicant had somehow gotten here without an earlier
        // group genuinely saved) — it never turns it on by itself.
        const newFieldOverrides: Record<string, any> = {};
        newFields.forEach((f: any) => {
          if (f?.name) newFieldOverrides[f.name] = "";
        });
        syncContinueButtonEnabled(newFieldOverrides);
      } else {
        dispatch(setStatusPopup({ status: "error", message: "Unable to add Collateral fields right now. Please try again." }));
      }
    } catch (error: any) {
      dispatch(setStatusPopup({ status: "error", message: error?.response?.data?.message || "Unable to add Collateral fields right now. Please try again." }));
    } finally {
      setIsAddingCollateral(false);
    }
  };

  // DIY journey only: if the applicant (re-)selects Yes while no collateral
  // exists at all (e.g. right after deleting the last one auto-switched the
  // answer to No, and they change their mind back to Yes), Collateral 1
  // should load automatically instead of requiring an extra "Add More" click.
  useEffect(() => {
    if (!isDIYJourney) return;
    if (!isHasCollateralYes) return;
    if (collateralFields.length > 0) return;
    if (isAddingCollateral) return;
    if (!trackingId) return;
    // The delete-all-to-zero effect above just ran in this same pass and
    // switched the answer to No — isHasCollateralYes here is still the
    // stale (pre-switch) value, so without this check this effect would
    // immediately recreate a blank Collateral 1 right after it was cleared.
    if (justAutoSwitchedToNoRef.current) {
      justAutoSwitchedToNoRef.current = false;
      return;
    }
    addCollateralFields();
  }, [isDIYJourney, isHasCollateralYes, collateralFields.length, trackingId]);

  const removeCollateralFields = async (n: number) => {
    const group = getFieldsByGroup(n);
    const allCollateralFields = [...group.base, ...group.coOwner];
    // Field records from an account still on the older combined
    // "Reference & Collateral Details" backend shape can carry their
    // identifier under formFieldId/fieldId instead of id (see
    // resolveCollateralFieldId) — falling back to those keeps this working
    // for journey-created collaterals, not just ones added from Summary.
    const fieldIds = allCollateralFields
      .map((f: any) => resolveCollateralFieldId(f))
      .filter((id: any) => id !== undefined && id !== null);
    const fieldNames = new Set(allCollateralFields.map((f: any) => f?.name));

    if (!trackingId) {
      dispatch(setStatusPopup({ status: "error", message: "Unable to remove Collateral fields right now. Please try again." }));
      return;
    }

    setIsRemovingCollateral((prev) => ({ ...prev, [n]: true }));
    // In the SUMMARY edit flow, deleting a collateral only updated
    // state.stepper.horizontalSteps below — that's in-memory only (not
    // persisted), so it made the current session look right but never
    // refreshed fetchUserApplicationData (what the summary re-derives its
    // collateral list from after a refetch/refresh). Toggling
    // summarySaveInProgress true->false is the same signal ApplicationDetails.tsx
    // already reacts to after a save, forcing it to refetch immediately.
    const isSummaryMode = pageType === "SUMMARY";
    if (isSummaryMode) {
      dispatch(setSummarySaveInProgress(true));
    }
    try {
      let payload = {
        "trackingId":trackingId,
        "collateralSequenceType": `COLLATERAL_${
          numberToWord[n] || n
          }`,
        "eventName":"DELETE_COLLATERAL",
      }
      // The LOS-domain delete only needs trackingId/collateralSequenceType,
      // not field ids — it must fire even when no field id could be
      // resolved, otherwise a collateral with unresolvable ids (see the
      // fallback above) never gets deleted from the DB at all.
      const res=await postsServices.addPost("api/v1/processio/delete-collateral",payload);
      // Deleting Collateral 1 must not call create-collateral (not even with
      // blank values) — delete-collateral above is the only API call here.
      const resp =
        fieldIds.length > 0 && n>1
          ? await postsServices.addPost("api/v1/product/delete-user-fields", {
              form_field_ids: fieldIds,
              trackingId,
            })
          : null;

      if (resp && resp?.data?.status !== "success") {
        dispatch(setStatusPopup({ status: "error", message: "Unable to remove Collateral fields right now. Please try again." }));
        if (isSummaryMode) dispatch(setSummarySaveInProgress(false));
        //return;
      }

      // Collateral 1's field keys are treated as permanent (part of the
      // get-data API's default schema — see the delete-user-fields skip
      // above): "removing" it only clears its data (LOS delete above +
      // blanking its Formik values below), never its field definitions, so
      // re-selecting Yes later can just show them again instead of calling
      // the create API to recreate duplicates. Collateral 2+ has no such
      // default — removing it strips its keys entirely, same as before.
      if (n > 1) {
        // Don't index state.stepper.horizontalSteps with activeHorizontalIndex —
        // in the SUMMARY edit flow that's the summary page's own local card
        // index (4, for "Additional Details & Documents"), not the real
        // backend/DynamicForm index (3) that horizontalSteps is actually built
        // with. Indexing at [4] silently hit an empty/undefined step, so the
        // bounds check below always failed and this dispatch never fired —
        // the field never actually got removed from horizontalSteps, so it
        // reappeared (from the DB's perspective correctly deleted, but the
        // locally-held field config still had it) the next time this section
        // remounted. Resolve the real location by stepLabel instead, mirroring
        // how addCoOwnerFields/addCollateralFields already resolve
        // verticalStepId for the delete/add API calls.
        const updatedHorizontalSteps = [...(horizontalSteps || [])];
        let resolvedHorizontalIndex = -1;
        let resolvedVerticalIndex = -1;
        updatedHorizontalSteps.forEach((hStep: any, hIdx: number) => {
          if (resolvedHorizontalIndex !== -1) return;
          const vIdx = (hStep?.verticalSteps || []).findIndex(
            (vStep: any) => vStep?.stepLabel === "Collateral Details",
          );
          if (vIdx !== -1) {
            resolvedHorizontalIndex = hIdx;
            resolvedVerticalIndex = vIdx;
          }
        });

        if (resolvedHorizontalIndex !== -1 && resolvedVerticalIndex !== -1) {
          const targetHorizontal = { ...updatedHorizontalSteps[resolvedHorizontalIndex] };
          const targetVerticalSteps = [...(targetHorizontal.verticalSteps || [])];
          const targetStep = { ...targetVerticalSteps[resolvedVerticalIndex] };
          targetStep.fields = (targetStep.fields || []).filter((f: any) => !fieldNames.has(f?.name));
          targetVerticalSteps[resolvedVerticalIndex] = targetStep;
          targetHorizontal.verticalSteps = targetVerticalSteps;
          updatedHorizontalSteps[resolvedHorizontalIndex] = targetHorizontal;
          dispatch(setHorizontalSteps(updatedHorizontalSteps));
        }
        setPendingRemovedFieldNames((prev) => {
          const next = { ...prev };
          fieldNames.forEach((name) => {
            if (name) next[String(name)] = true;
          });
          return next;
        });
        setPendingAddedFields((prev) =>
          prev.filter((f: any) => !fieldNames.has(f?.name)),
        );
      }

      // Collateral 1's BASE property fields + its first co-owner section
      // are the permanent default schema (see the n>1 gate/comment above),
      // but any additional co-owner sections (2nd, 3rd, ...) were created
      // the same way collateral 2+'s fields are — via store-user-fields,
      // through addCoOwnerFields — so they're not part of that permanent
      // schema and must actually be deleted here, the same way
      // removeCoOwnerSection deletes a single extra section. Otherwise they
      // only get hidden, not removed, and reappear in full the next time
      // this collateral is re-added.
      if (n === 1) {
        const coOwnerSectionsForRemoval = splitCoOwnerSections(group.coOwner);
        const extraCoOwnerFields = coOwnerSectionsForRemoval.slice(1).flat();
        const extraCoOwnerFieldIds = extraCoOwnerFields
          .map((f: any) => resolveCollateralFieldId(f))
          .filter((id: any) => id !== undefined && id !== null);
        const extraCoOwnerFieldNames = new Set(
          extraCoOwnerFields.map((f: any) => f?.name),
        );

        if (extraCoOwnerFieldIds.length > 0) {
          try {
            await postsServices.addPost("api/v1/product/delete-user-fields", {
              form_field_ids: extraCoOwnerFieldIds,
              trackingId,
            });
          } catch (extraCoOwnerDeleteError) {
            // Non-fatal — still strip them from horizontalSteps below so
            // the UI doesn't keep showing them even if this call failed; a
            // stray backend field def is a lesser problem than the extra
            // sections reappearing in full on every re-add.
          }

          const updatedHorizontalStepsForCoOwners = [...(horizontalSteps || [])];
          let resolvedCoOwnerHorizontalIndex = -1;
          let resolvedCoOwnerVerticalIndex = -1;
          updatedHorizontalStepsForCoOwners.forEach((hStep: any, hIdx: number) => {
            if (resolvedCoOwnerHorizontalIndex !== -1) return;
            const vIdx = (hStep?.verticalSteps || []).findIndex(
              (vStep: any) => vStep?.stepLabel === "Collateral Details",
            );
            if (vIdx !== -1) {
              resolvedCoOwnerHorizontalIndex = hIdx;
              resolvedCoOwnerVerticalIndex = vIdx;
            }
          });
          if (resolvedCoOwnerHorizontalIndex !== -1 && resolvedCoOwnerVerticalIndex !== -1) {
            const targetHorizontal = {
              ...updatedHorizontalStepsForCoOwners[resolvedCoOwnerHorizontalIndex],
            };
            const targetVerticalSteps = [...(targetHorizontal.verticalSteps || [])];
            const targetStep = { ...targetVerticalSteps[resolvedCoOwnerVerticalIndex] };
            targetStep.fields = (targetStep.fields || []).filter(
              (f: any) => !extraCoOwnerFieldNames.has(f?.name),
            );
            targetVerticalSteps[resolvedCoOwnerVerticalIndex] = targetStep;
            targetHorizontal.verticalSteps = targetVerticalSteps;
            updatedHorizontalStepsForCoOwners[resolvedCoOwnerHorizontalIndex] = targetHorizontal;
            dispatch(setHorizontalSteps(updatedHorizontalStepsForCoOwners));
          }
          setPendingRemovedFieldNames((prev) => {
            const next = { ...prev };
            extraCoOwnerFieldNames.forEach((name) => {
              if (name) next[String(name)] = true;
            });
            return next;
          });
          setPendingAddedFields((prev) =>
            prev.filter((f: any) => !extraCoOwnerFieldNames.has(f?.name)),
          );
          setVisibleCoOwnerCount((prev) => ({ ...prev, [n]: 1 }));
        }
      }

      if (formikRef?.current) {
        allCollateralFields.forEach((field: any) => {
          formikRef.current.setFieldValue(field.name, undefined, false);
        });
      }
      forgetSavedOwnershipPercentages(fieldNames);
      // Clearing Formik alone isn't enough for Collateral 1 — its field
      // keys stay behind (see above), and redux formData is what re-seeds
      // Formik's initialValues, so leftover values there would reappear
      // the moment the applicant selects Yes again.
      if (allCollateralFields.length > 0) {
        const clearedFormData: Record<string, undefined> = {};
        allCollateralFields.forEach((field: any) => {
          if (field?.name) clearedFormData[field.name] = undefined;
        });
        dispatch(updateFormData(clearedFormData));
        // Re-check Continue right away using the values this remove just
        // set, rather than waiting for DynamicForm's own reconcile effect —
        // that effect only re-runs off formData/fields/horizontalSteps
        // changes, and group 1's removal here (its field keys stay behind
        // on purpose, see above) never touches any of those for n===1.
        syncContinueButtonEnabled(clearedFormData);
      }
      // "Has Collateral?" must revert to No when the last collateral GROUP
      // is removed, even if that group never had any data typed into it —
      // the auto-switch effect above only fires on a
      // collateralNumbersWithData transition from >0 to 0, but an empty
      // group's data-count was already 0 before this remove, so 0 -> 0
      // never trips that effect's dependency. Checked against
      // realCollateralNumbers (group KEYS that count as a real collateral),
      // not collateralNumbersWithData (which have DATA) — e.g. Collateral 1
      // + an "Add More"-generated but still-blank Collateral 2 must NOT
      // switch to No when Collateral 1 is removed, since Collateral 2's
      // group still exists and is presumably still being filled in. But
      // Collateral 1's own bare field keys never count towards this on
      // their own (see realCollateralNumbers) — e.g. the get-data API
      // returning both Collateral 1 and 2's field keys while neither is
      // actually filled in yet (so Collateral 2 is the one shown in the
      // UI): removing that Collateral 2 must still switch to No, ignoring
      // Collateral 1's permanent-but-empty placeholder.
      if (
        hasCollateralField &&
        isHasCollateralYes &&
        realCollateralNumbers.filter((num) => num !== n).length === 0
      ) {
        formikRef?.current?.setFieldValue(hasCollateralField.name, "No", false);
        dispatch(updateFormData({ [hasCollateralField.name]: "No" }));
      }
      // SUMMARY edit flow (and anywhere else that re-derives values from
      // the raw fetch snapshot instead of formData — see
      // fetchedCollateralTypeMap's comment above) reads saved values off
      // fetchUserApplicationData.horizontalSteps directly, not formData —
      // clearing formData alone left this deleted collateral's values
      // there, so selecting Yes again picked the stale, already-deleted
      // data back up.
      if (
        fieldNames.size > 0 &&
        fetchUserApplicationData &&
        Array.isArray(fetchUserApplicationData?.horizontalSteps)
      ) {
        const updatedFetchedSteps = fetchUserApplicationData.horizontalSteps.map(
          (hStep: any) => {
            if (!Array.isArray(hStep?.verticalSteps)) return hStep;
            const updatedVerticalSteps = hStep.verticalSteps.map((vStep: any) => {
              if (!Array.isArray(vStep?.fields)) return vStep;
              const updatedFields = vStep.fields.filter((fieldObj: any) => {
                if (!fieldObj || typeof fieldObj !== "object") return true;
                const key = Object.keys(fieldObj)[0];
                return !fieldNames.has(key);
              });
              return { ...vStep, fields: updatedFields };
            });
            return { ...hStep, verticalSteps: updatedVerticalSteps };
          },
        );
        dispatch(
          setFetchUserApplicationData({
            ...fetchUserApplicationData,
            horizontalSteps: updatedFetchedSteps,
          }),
        );
      }

      setExpanded(displayedCollateralNumbers.find((num) => num !== n) ?? 1);

      if (isSummaryMode) {
        //dispatch(bumpFormJsonRefreshNonce());
        dispatch(setSummarySaveInProgress(false));
      }
    } catch (error: any) {
      dispatch(setStatusPopup({ status: "error", message: error?.response?.data?.message || "Unable to remove Collateral fields right now. Please try again." }));
      if (isSummaryMode) dispatch(setSummarySaveInProgress(false));
    } finally {
      setIsRemovingCollateral((prev) => ({ ...prev, [n]: false }));
    }
  };

  const openConfirmRemoveCollateral = (collateralNumber: number) => {
    setPendingRemove({ kind: "collateral", collateralNumber });
    setConfirmRemoveOpen(true);
  };

  const openConfirmRemoveCoOwner = (
    collateralNumber: number,
    sectionFields: any[],
    totalSections: number,
    sectionIdx: number,
  ) => {
    const removeKey = `${collateralNumber}-${sectionIdx}`;
    setPendingRemove({
      kind: "coOwner",
      collateralNumber,
      sectionFields,
      totalSections,
      sectionIdx,
      removeKey,
    });
    setConfirmRemoveOpen(true);
  };

  const handleCancelRemove = () => {
    setConfirmRemoveOpen(false);
    setPendingRemove(null);
    setIsConfirmRemoving(false);
  };

  const handleConfirmRemove = async () => {
    if (!pendingRemove || isConfirmRemoving) return;
    setIsConfirmRemoving(true);
    const action = pendingRemove;
    setConfirmRemoveOpen(false);
    setPendingRemove(null);
    try {
      if (action.kind === "collateral") {
        await removeCollateralFields(action.collateralNumber);
      } else {
        await removeCoOwnerSection(
          action.collateralNumber,
          action.sectionFields,
          action.totalSections,
          action.sectionIdx,
        );
      }
    } finally {
      setIsConfirmRemoving(false);
    }
  };

  const addCoOwnerFields = async (collateralNumber: number, templateSection: any[]) => {
    if (!Array.isArray(templateSection) || templateSection.length === 0) {
      return;
    }
    
    // Resolve the real horizontal/vertical index by scanning for the actual
    // "Collateral Details" step (same fix as addCollateralFields above) —
    // falling back to a hardcoded/assumed index when it's genuinely absent
    // risks writing these new co-owner fields into an unrelated step.
    let resolvedHorizontalIndex = -1;
    let resolvedVerticalIndex = -1;
    let verticalStepId: any;
    (horizontalSteps || []).forEach((hStep: any, hIdx: number) => {
      if (resolvedHorizontalIndex !== -1) return;
      const vIdx = (hStep?.verticalSteps || []).findIndex(
        (vStep: any) => vStep?.stepLabel === "Collateral Details",
      );
      if (vIdx !== -1) {
        resolvedHorizontalIndex = hIdx;
        resolvedVerticalIndex = vIdx;
        verticalStepId = hStep.verticalSteps[vIdx]?.id;
      }
    });

    if (!verticalStepId || !trackingId) {
      dispatch(
        setStatusPopup({
          status: "error",
          message: "Unable to add Co-Owner fields right now. Please try again.",
        }),
      );
      return;
    }

    const groupedForCollateral = getFieldsByGroup(collateralNumber);
    const ownerSections = splitCoOwnerSections(groupedForCollateral.coOwner);
    const existingCoOwnerFieldNames = new Set(
      groupedForCollateral.coOwner.map((f: any) => String(f?.name || "")),
    );
    let nextOwnerIndex = Math.max(ownerSections.length, 0) + 1;
    // Guard against the newly-renamed fields colliding with an existing
    // owner's field name — a collision would make Formik treat both owners
    // as the same field, so filling in the new section silently overwrites
    // (and looks like it removes) the previous owner's data.
    while (
      templateSection.some((field: any) =>
        existingCoOwnerFieldNames.has(
          renameCoOwnerField(String(field?.name || ""), collateralNumber, nextOwnerIndex),
        ),
      )
    ) {
      nextOwnerIndex += 1;
    }

    const apiFields = templateSection.map((field: any) => {
      const originalName = String(field?.name || "");

      return {
        label: field?.label,
        name: renameCoOwnerField(
              originalName,
              collateralNumber,
              nextOwnerIndex,
            ),
        type: field?.type,
        required: field?.required,
        options: field?.options ?? null,
      };
    });

    setIsAddingCoOwner((prev) => ({ ...prev, [collateralNumber]: true }));

    try {
      const resp = await postsServices.addPost("api/v1/product/store-user-fields", {
        vertical_step_id: verticalStepId,
        trackingId,
        fields: apiFields,
      });

      if (resp?.data?.status === "success" && Array.isArray(resp?.data?.data)) {
        const newFields = resp.data.data.map((f: any) => ({
          ...f,
          options: f?.options ? f?.options : null,
        }));

        const updatedHorizontalSteps = [...(horizontalSteps || [])];
        const targetHorizontal = { ...(updatedHorizontalSteps?.[resolvedHorizontalIndex] || {}) };
        const targetVerticalSteps = [...(targetHorizontal?.verticalSteps || [])];

        const targetStep = { ...targetVerticalSteps[resolvedVerticalIndex] };
        targetStep.fields = [...(targetStep.fields || []), ...newFields];
        targetVerticalSteps[resolvedVerticalIndex] = targetStep;
        targetHorizontal.verticalSteps = targetVerticalSteps;
        updatedHorizontalSteps[resolvedHorizontalIndex] = targetHorizontal;
        dispatch(setHorizontalSteps(updatedHorizontalSteps));
        setPendingAddedFields((prev) => {
          const byName = new Map<string, any>();
          [...prev, ...newFields].forEach((f: any) => {
            byName.set(String(f?.name || ""), f);
          });
          return Array.from(byName.values());
        });
        setPendingRemovedFieldNames((prev) => {
          const next = { ...prev };
          newFields.forEach((f: any) => {
            const name = String(f?.name || "");
            if (name in next) delete next[name];
          });
          return next;
        });

        if (formikRef?.current) {
          newFields.forEach((field: any) => {
            // A newly added co-owner section must always start blank — auto-selecting
            // the first autocomplete option here previously pre-filled the new
            // Co-Owner's Name with an existing co-owner's name (the first entry in
            // the shared name options pool), making the new section look like a
            // duplicate of Co-Owner 1 once other co-owners already existed.
            const defaultValue: any = field?.type === "boolean" ? false : "";
            formikRef.current.setFieldValue(field.name, defaultValue, false);
          });
        }

        setVisibleCoOwnerCount((prev) => ({
          ...prev,
          [collateralNumber]: (prev[collateralNumber] ?? ownerSections.length) + 1,
        }));
      } else {
        dispatch(
          setStatusPopup({
            status: "error",
            message: "Unable to add Co-Owner fields right now. Please try again.",
          }),
        );
      }
    } catch (error: any) {
      dispatch(
        setStatusPopup({
          status: "error",
          message:
            error?.response?.data?.message ||
            "Unable to add Co-Owner fields right now. Please try again.",
        }),
      );
    } finally {
      setIsAddingCoOwner((prev) => ({ ...prev, [collateralNumber]: false }));
    }
  };

  const enrichCollateralField = (field: any): any => {
      const normalized = getNormalized(field?.name);
      // Always read the latest Formik values to avoid stale closure issues
      const latestValues = formikRef?.current?.values || formValues;

      // Address Line 2 is always optional for Property collateral (it's the
      // only visible base field excluded from getCollateralRequiredFieldErrorsForGroup's
      // required checks) — override the raw field config's `required` flag
      // so its rendered asterisk matches that, instead of always showing as
      // mandatory regardless of collateral type.
      if (/^collateral\d*addressline2$/.test(normalized)) {
        return { ...field, required: false };
      }

      const withCollateralTypeLock = (nextField: any) => {
        const typeMatch = normalized.match(/^collateral(\d*)type$/);
        if (!typeMatch) return nextField;
        const collateralNumber = Number(typeMatch[1] || 1);
        // fetchedCollateralTypeMap reflects the LAST fetch, not "right now" —
        // after deleting Collateral 1 (which blanks its value but keeps its
        // field keys) and re-selecting Yes, it re-shows blank but this map
        // can still be stale, wrongly locking Type with nothing to protect.
        // Only lock when the group also currently has real live data.
        const isLocked =
          (fetchedCollateralTypeMap[collateralNumber] || savedCollateralTypeMap[collateralNumber]) &&
          collateralNumbersWithData.includes(collateralNumber);
        return isLocked ? { ...nextField, disabled: true } : nextField;
      };

      // Exclude selected propertyType in Collateral N from Collateral N+1 dropdown
      if (normalized.includes("propertysubtypename")) {
        return withCollateralTypeLock({ ...field, options: { options: propertySubTypeOptions } });
      }
      if (normalized.includes("propertypename") && !normalized.includes("subtype")) {
        const sectionMatch = normalized.match(/^collateral(\d+)/);
        const sectionNum = sectionMatch ? sectionMatch[1] : null;
        let filteredOptions = propertyTypeOptions;
        if (sectionNum && sectionNum !== "1") {
          const collateral1PropertyField = collateralFields.find((f: any) => {
            const n = getNormalized(f?.name);
            // startsWith("collateral1") also matches collateral10/11/12/...
            // property fields, since string-prefix matching doesn't respect
            // number boundaries — use the number-aware helper instead so
            // collateral12's propertyName never gets mistaken for collateral1's.
            return (
              n.includes("propertypename") &&
              !n.includes("subtype") &&
              !n.startsWith("coownercollateral") &&
              fieldBelongsToCollateralNumber(f?.name, 1)
            );
          });
          const raw = collateral1PropertyField ? latestValues?.[collateral1PropertyField.name] : undefined;
          const selectedValue = raw && typeof raw === "object" ? raw.value : raw;
          if (selectedValue !== undefined && selectedValue !== null && String(selectedValue).trim() !== "") {
            filteredOptions = propertyTypeOptions.filter(
              (opt) => String(opt.value) !== String(selectedValue),
            );
          }
        }
        return withCollateralTypeLock({ ...field, options: { options: filteredOptions } });
      }
      if (normalized.endsWith("addresstype")) {
        return withCollateralTypeLock({ ...field, options: { options: addressTypeOptions } });
      }

      if (normalized.includes("totalvalue")) {
        const sectionMatch = normalized.match(/^collateral(\d+)/);
        const sectionNum = sectionMatch ? Number(sectionMatch[1]) : 1;
        const { base } = getFieldsByGroup(sectionNum);
        const autoTotal = getAutoComputedPropertyTotal(base, latestValues);
        if (autoTotal?.totalValueFieldName === field.name) {
          return { ...field, disabled: true };
        }
      }

      // FD Holder Name ("fdholdername") and Savings Holder Name ("holdername") both
      // contain the "holdername" token — lock once Student Details has been filled in.
      if (normalized.includes("holdername") && isStudentStepCompleted) {
        return { ...field, disabled: true };
      }

      return withCollateralTypeLock(field);
  };

  const isFilled = (value: any): boolean => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "number") return true;
    if (typeof value === "boolean") return true;
    if (value && typeof value === "object") {
      if ("value" in value) return String(value.value ?? "").trim() !== "";
      return Object.keys(value).length > 0;
    }
    return String(value ?? "").trim() !== "";
  };

  const getCompletion = (sectionFields: any[]) => {
    const required = sectionFields.filter((f: any) => f?.required !== false);
    const filled = required.filter((f: any) =>
      isFilled(formValues?.[f?.name]),
    ).length;
    return `${filled} / ${required.length}`;
  };

  const fixedColumnGridSx = {
    // Force a consistent 3-column layout on >= sm for fields rendered by parent renderField.
    "@media (min-width:600px)": {
      "& > .MuiGrid-item": {
        flexBasis: "33.333333%",
        maxWidth: "33.333333%",
      },
    },
  };

  const baseGridSx = {
    ...fixedColumnGridSx,
    "@media (min-width:600px)": {
      ...fixedColumnGridSx["@media (min-width:600px)"],
      // Keep first field on a separate row, but preserve same visual control width.
      "& > .MuiGrid-item:first-of-type": {
        flexBasis: "100%",
        maxWidth: "100%",
      },
      "& > .MuiGrid-item:first-of-type > .MuiBox-root": {
        width: "33.333333%",
      },
    },
  };
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  // "Add More" is enabled only when all required fields of the last collateral section are filled
  const lastCollateralNum = displayedCollateralNumbers[displayedCollateralNumbers.length - 1];
  const isLastCollateralComplete = useMemo(() => {
    if (!lastCollateralNum) return false;
    const { base, coOwner } = getFieldsByGroup(lastCollateralNum);
    const visibleBase = getVisibleBaseFields(base);
    const coOwnerSections = splitCoOwnerSections(coOwner);
    const visibleCoOwner = coOwnerSections.flat();
    const allSectionFields = [...visibleBase, ...visibleCoOwner];
    // Collateral Type only needs to be picked once the applicant has said
    // they're adding collateral — don't block "Add More" on it beforehand.
    // (isHasCollateralYes, not the legacy Loan Details "collateral"
    // dropdown, which the hasCollateral-based flow no longer keeps in sync.)
    const isCollateralTypeField = (f: any) =>
      /^collateral\d*Type$/i.test(String(f?.name || ""));
    const requiredFields = allSectionFields.filter(
      (f: any) =>
        f?.required !== false &&
        !(isCollateralTypeField(f) && !isHasCollateralYes),
    );
    if (requiredFields.length === 0) return true;
    return requiredFields.every((f: any) => isFilled(formValues?.[f?.name]));
  }, [lastCollateralNum, collateralFields, formValues, isHasCollateralYes]);
  // Before any collateral section exists, "Add More" (which creates
  // Collateral 1) is gated on "Has Collateral?" being answered Yes instead
  // of on section-completeness, since there's no section yet to check.
  const isAddMoreEnabled =
    collateralFields.length === 0 ? isHasCollateralYes : isLastCollateralComplete;
  // No collateral group exists yet and the applicant said No — nothing to
  // add, so show a plain Save & Next (persists the "No" answer) instead of
  // the Add More affordance.
  const showNoCollateralSaveAndNext =
    collateralFields.length === 0 && isHasCollateralNo;
  return (
    <Box
      sx={{
        width: "100%",
        marginTop: "6px",
        marginLeft: pageType === "SUMMARY"|| isXsOrSm ? "10px" : 0,
      }}
    >
      {hasCollateralField && (
        <Box
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            p: 2,
            mb: 2,
          }}
        >
          <Grid container spacing={2}>
            <React.Fragment key={hasCollateralField?.id || hasCollateralField?.name}>
              {renderField(hasCollateralField)}
            </React.Fragment>
          </Grid>

        </Box>
      )}

      {!showNoCollateralSaveAndNext && displayedCollateralNumbers.length < 5 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 1,
            pb: 1,
            px: { xs: 1, sm: 0},
            minHeight: { xs: "0px", sm: "70px" },
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: "600",
              fontSize: "13px",
              color: isAddMoreEnabled ? "#00B3B5" : "#b0b0b0",
              display: "flex",
              alignItems: "center",
              cursor: isAddMoreEnabled ? "pointer" : "not-allowed",
              pointerEvents: isAddMoreEnabled ? "auto" : "none",
              userSelect: "none",
            }}
            onClick={isAddMoreEnabled ? addCollateralFields : undefined}
          >
            {isAddingCollateral ? (
              "Adding..."
            ) : (
              <>
                <AddIcon /> Add More
              </>
            )}
          </Typography>
        </Box>
      )}

      {displayedCollateralNumbers.map((n, i) => {
        const grouped = getFieldsByGroup(n);
        const visibleBaseFields = getVisibleBaseFields(grouped.base);
        const coOwnerSections = splitCoOwnerSections(grouped.coOwner);
        const currentVisibleCoOwnerCount = Math.min(
          Math.max(visibleCoOwnerCount[n] ?? coOwnerSections.length, 1),
          Math.max(coOwnerSections.length, 1),
        );
        const visibleCoOwnerSections = coOwnerSections.slice(0, currentVisibleCoOwnerCount);
        const visibleCoOwnerFields = visibleCoOwnerSections.flat();
        const allFields = [...visibleBaseFields, ...visibleCoOwnerFields];
        const editedCoOwnerPercentageFieldName =
          getEditedCoOwnerPercentageFieldName(coOwnerSections);
        // Update flow: at least one co-owner of this collateral is already saved.
        const hasSavedCoOwner = coOwnerSections.some(isCoOwnerSectionFetched);

        return (
          <Box key={n} sx={{ mb: 2, }}>
            <Accordion
              expanded={expanded === n}
              onChange={() => setExpanded(expanded === n ? false : n)}
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: "8px !important",
                boxShadow: "none",
                minHeight:{ xs: "60px", sm: "70px" },
                "&:before": { display: "none" },
              }}
            >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", pr: 1 }}>
                <Typography fontWeight={600} fontSize="15px" marginTop={"7px"}>
                  Collateral {i + 1}
                </Typography>
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ p: 2 }}>
              <Grid container spacing={2} sx={baseGridSx}>
                {visibleBaseFields.map((field: any) => (
                  <React.Fragment key={field?.id || field?.name}>
                    {renderField(enrichCollateralField(field))}
                  </React.Fragment>
                ))}
              </Grid>

              {grouped.coOwner.length > 0 && (
                <>
                  <Box
                    sx={{
                      mt: 2.5,
                      mb: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "14px", fontWeight: 700, color: "#6f6b7d" }}
                    >
                      Co-Owner Information
                    </Typography>
                    <Button
                      sx={{
                        textTransform: "none",
                        fontFamily: "Jost, sans-serif",
                        fontWeight: 600,
                        fontSize: "13px",
                        color: "#33c2c4",
                        minWidth: "auto",
                        p: 0,
                        "&:hover": { backgroundColor: "transparent" },
                      }}
                      onClick={() => {
                        addCoOwnerFields(n, coOwnerSections?.[0] || []);
                      }}
                      disabled={Boolean(isAddingCoOwner[n]) || areAllCoOwnerNamesSelected(n, visibleCoOwnerSections.length)}
                    >
                      <AddIcon sx={{ mr: 0.4, fontSize: "16px" }} />
                      {isAddingCoOwner[n] ? "Adding..." : "Add More"}
                    </Button>
                  </Box>

                  {hasSavedCoOwner && (
                    <Box
                      sx={{
                        mb: 2,
                        px: 1.5,
                        py: 1,
                        backgroundColor: "#FFF4CC",
                        border: " #FFF4CC",
                        borderRadius: "6px",
                      }}
                    >
                      <Typography sx={{ fontSize: "13px", color: "#7A5A00" }}>
                        You can only update one co-owner at a time.
                      </Typography>
                    </Box>
                  )}

                  {visibleCoOwnerSections.map((sectionFields: any[], sectionIdx: number) => (
                    <Box key={`co-owner-section-${n}-${sectionIdx}`} sx={{ mb: sectionIdx === visibleCoOwnerSections.length - 1 ? 0 : 2 }}>
                      <Typography sx={{ fontSize: "13px", color: "#6f6b7d", mb: 1.5 }}>
                        Co-Owner {sectionIdx + 1}
                      </Typography>

                      <Grid container spacing={2} sx={fixedColumnGridSx}>
                        {sectionFields.map((field: any) => (
                          <React.Fragment key={field?.id || field?.name}>
                            {renderField(
                              withSavedCoOwnerPercentageLockOnAdd(
                                withCoOwnerPercentageLock(field, editedCoOwnerPercentageFieldName),
                                sectionFields,
                                visibleCoOwnerSections,
                              ),
                            )}
                          </React.Fragment>
                        ))}
                      </Grid>

                      {sectionIdx > 0 && !isCoOwnerSectionFetched(sectionFields) && (
                        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                          <Button
                            sx={{
                              textTransform: "none",
                              fontFamily: "Jost, sans-serif",
                              fontWeight: 600,
                              fontSize: "13px",
                              color: "#ea5455",
                              minWidth: "auto",
                              p: 0,
                              "&:hover": { backgroundColor: "transparent" },
                            }}
                            onClick={() =>
                              openConfirmRemoveCoOwner(
                                n,
                                sectionFields,
                                coOwnerSections.length,
                                sectionIdx,
                              )
                            }
                            disabled={Boolean(isRemovingCoOwner[`${n}-${sectionIdx}`])}
                          >
                            <RemoveCircleOutlineIcon sx={{ mr: 0.4, fontSize: "16px" }} />
                            {isRemovingCoOwner[`${n}-${sectionIdx}`] ? "Removing..." : "Remove"}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  ))}
                </>
              )}

              <Typography
                sx={{
                  mt: 1.5,
                  color: "#6f6b7d",
                  fontSize: "12px",
                  textAlign: "right",
                }}
              >
                Field Completion {getCompletion(allFields)}
              </Typography>

              {!hideInternalSaveButton && (
                <Box sx={{ mt: 2.5, display: "flex", justifyContent: "flex-start" }}>
                  <PrimaryButton
                    type="button"
                    disabled={Boolean(isSaving)}
                    onClick={async () => {
                      try {
                        const result = onSaveAndNext
                          ? await onSaveAndNext(n)
                          : undefined;
                        if (result === false) return;
                        const nextIdx = displayedCollateralNumbers[displayedCollateralNumbers.indexOf(n) + 1];
                        if (nextIdx !== undefined) setExpanded(nextIdx);
                        else {
                          // setExpanded(false);
                          // dispatch(setActiveHorizontalIndex(3));
                          // dispatch(setActiveVerticalIndex(4));
                        }
                      } catch (error) {
                        // Save errors are handled by parent callbacks.
                      }
                    }}
                  >
                    {isSaving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Save & Next"}
                  </PrimaryButton>
                </Box>
              )}
            </AccordionDetails>
            </Accordion>

            {
              <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-start" }}>
                <Button
                  size="small"
                  sx={{
                    textTransform: "none",
                    fontFamily: "Jost, sans-serif",
                    fontWeight: 600,
                    fontSize: "12px",
                    color: "#ea5455",
                    minWidth: "auto",
                    p: 0,
                    "&:hover": { backgroundColor: "transparent" },
                  }}
                  onClick={() => {
                    openConfirmRemoveCollateral(n);
                  }}
                  disabled={Boolean(isRemovingCollateral[n])}
                >
                  <RemoveCircleOutlineIcon sx={{ mr: 0.4, fontSize: "15px" }} />
                  {isRemovingCollateral[n] ? "Removing..." : "Remove"}
                </Button>
              </Box>
            }
          </Box>
        );
      })}

      {/* {collateralFields.length === 0 && (
        <Typography color="text.secondary" sx={{ p: 2 }}>
          No collateral fields available.
        </Typography>
      )} */}

      <ModalPopup
        isOpen={confirmRemoveOpen}
        onClose={handleCancelRemove}
        title="Remove"
        subTitle={
          pendingRemove?.kind === "collateral" ? "Remove Collateral" : "Remove Co-Owner"
        }
        style={{ width: "100%" }}
      >
        <Box sx={{ p: 2 }}>
          <Typography>
            Are you sure you want to remove this{" "}
            {pendingRemove?.kind === "collateral" ? "Collateral" : "Co-Owner"}?
            This action cannot be undone.
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 2 }}>
            <Button
              onClick={handleCancelRemove}
              color="primary"
              size="small"
              variant="outlined"
              disabled={isConfirmRemoving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmRemove}
              size="small"
              variant="contained"
              sx={{
                backgroundColor: "#00b3b5",
                "&:hover": { backgroundColor: "#00b3b5" },
              }}
              disabled={isConfirmRemoving}
            >
              {isConfirmRemoving ? "Removing..." : "Confirm"}
            </Button>
          </Box>
        </Box>
      </ModalPopup>
    </Box>
  );
};

export default CollateralDetails;